openapi: 3.0.0
servers:
  - url: https://{API_SERVER_DOMAIN}.directcloud.jp
    variables:
      API_SERVER_DOMAIN:
        default: dapi
        enum:
          - dapi
          - api

info:
  version: "1.0.0"
  title: DirectCloud Manager API Reference for 3rd Party
  description: |
    ### この文書は、DirectCloudサービスと3rdPartyアプリ、またはウェブサイト連携を目的に開発されたAPI案内文書です。

    - サービスの安定のために開発APIサーバーと運営APIサーバーを分けて運営しますので、ご参考ください。

    - 文書の**{API_SERVER_DOMAIN}**に使用されるドメインは別途ご案内いたします。

    ## Requests
    ### `lang` パラメータ
    * 該当パラメータはheader、query、form から抽出するため、楽な方法で要請してください。

    エラー応答はクライアントの要請に含まれている「lang」パラメータに依存するため、必ず送らなければなりません。

    ### アプリケーションからAPI呼び出し
    DirectCloud APIは標準的なHTTP方式を使用します。
    - 例）ユーザー固有キー3に該当する情報を取得するため、次のようなHTTPリクエストを送信します。
      ```shell
      GET https://{API_SERVER_DOMAIN}/openapp/m1/users/3
      ```
    <br>
    インターネットブラウザ以外のPCおよびモバイルアプリケーションからAPIを呼び出す際は、すべてのリクエストに次の媒介変数がHTTP ヘッダーに含まれなければなりません。
    *例）ヘッダーに含まれる媒介変数
      |媒介変数名|値|必須|説明|例|
      |---------|-|--|--|-|
      device_id|String|Y|デバイス毎の固有識別キー|ABCDEF-G123-4H56-7890-ACDEFGHIJ71
      device_name|String|Y|デバイス名|iPhone Simulator
      os_version|String|Y|デバイスOS名|iOS 7.0.3
      app_version|String|Y|APIを呼び出すAPP情報|DirectBox 1.0.0

    * 例) デバイス別に

      デバイス種類|device_id|device_name|os_version|app_version
      -|-|-|-|-
      PC Agent|49ed2c8c13xxx|DESKTOP-XXX|Windows 10|DirectBox 2.0.14
      IOS APP|D09D900B-xxxx|iPhone|iOS 10.3.2|DirectCloud 1.4.21.8
      Android APP|3548880620999|SM-N910K|Android+22|DirectBox+2.0.14


    ## Responses
    API応答は、JSONデータ形式を使用しています。

    ### `status_code`
    *基本的に全ての回答の「status_code」は「200」です。

    * ご回答の成功および失敗の可否は`success`で判断することができます。
      *すべての成功応答は、success値がtrueです。
      *すべての失敗した応答は、success値がfalseです。

      **成功した応答**
      ```json
      {
        "success": true, / 応答成功可否
        "result": {}, / 結果データ
      }
      ```

      **失敗した応答**
      ```json
      {
        "success": false, /応答成功可否
        "all": "error message"、/エラーメッセージ
        「result_code」:「error code」 / エラーコード
      }
      ```
      result_codeは、2つの場合に分けられます。
      - "00" : システムエラー
      - "01" : 認証エラー

    <br><details>
    <summary>文書変更履歴を詳しく見る<summary>

    ### 文書変更履歴
    - **2021-03-29** 最初 作成
    </detail>
    ---

security:
  - application:
      - read

tags:
  - name: Auth
    description: 認証API
  - name: Users
    description: ユーザーAPI
  - name: Groups
    description: 組織API
  - name: Relations
    description: ユーザーと組織関係API
  - name: Shared Boxes
    description: 共有フォルダAPI
  - name: Permission
    description: 共有フォルダ権限API

paths:
  /openapi/jauth/token:
    $ref: "./path/auth.yaml#/ManagerToken"

  /openapi/jauth/tokenExpire:
    $ref: "./path/auth.yaml#/ManagerTokenExpire"
  
  /openapp/m1/users/lists:
    $ref: "./path/manager.yaml#/UsersLists"

  /openapp/m1/users/create:
    $ref: "./path/manager.yaml#/UsersCreate"

  /openapp/m1/users/read/{user_seq}:
    $ref: "./path/manager.yaml#/UsersReadUserSeq"

  /openapp/m1/users/update/{user_seq}:
    $ref: "./path/manager.yaml#/UsersUpdateUserSeq"

  /openapp/m1/users/delete:
    $ref: "./path/manager.yaml#/UsersDelete"

  /openapp/m1/groups/lists/{group_seq}:
    $ref: "./path/manager.yaml#/GroupsListGroupSeq"

  /openapp/m1/groups/create:
    $ref: "./path/manager.yaml#/GroupsCreate"

  /openapp/m1/groups/read/{group_seq}:
    $ref: "./path/manager.yaml#/GroupsReadGroupSeq"

  /openapp/m1/groups/update/{group_seq}:
    $ref: "./path/manager.yaml#/GroupsUpdateGroupSeq"

  /openapp/m1/groups/delete:
    $ref: "./path/manager.yaml#/GroupsDelete"

  /openapp/m1/relations/lists/{user_seq}:
    $ref: "./path/manager.yaml#/RelationsListUserSeq"

  /openapp/m1/relations/create:
    $ref: "./path/manager.yaml#/RelationsCreate"

  /openapp/m1/relations/delete:
    $ref: "./path/manager.yaml#/RelationsDelete"

  /openapp/m1/sharedboxes/lists/{node}:
    $ref: "./path/manager.yaml#/SharedBoxesListsNode"

  /openapp/m1/sharedboxes/create/{node}:
    $ref: "./path/manager.yaml#/SharedBoxesCreateNode"

  /openapp/m1/sharedboxes/read/{node}:
    $ref: "./path/manager.yaml#/SharedBoxesReadNode"

  /openapp/m1/sharedboxes/update/{node}:
    $ref: "./path/manager.yaml#/SharedBoxesUpdateNode"

  /openapp/m1/sharedboxes/delete:
    $ref: "./path/manager.yaml#/SharedBoxesDelete"

  /openapp/m1/folder_permissions/lists/{node}:
    $ref: "./path/manager.yaml#/FoldersPermissionsListsNode"

  /openapp/m1/folder_permissions/create:
    $ref: "./path/manager.yaml#/FolderPermissionsCreate"

  /openapp/m1/folder_permissions/update:
    $ref: "./path/manager.yaml#/FolderPermissionsUpdate"

  /openapp/m1/folder_permissions/delete:
    $ref: "./path/manager.yaml#/FolderPermissionsDelete"
